import React, { useEffect, useState } from 'react';
import { useParams,useRouter, useSearchParams } from 'next/navigation';
import Button from 'components/commercetools-ui/atoms/button';
import Link from 'components/commercetools-ui/atoms/link';
import { useFormat } from 'helpers/hooks/useFormat'; 
import InstantSearch from 'components/HOC/InstantSearch';
import { useSearchBox, useInstantSearch, Configure } from 'react-instantsearch-hooks-web';
import { productsQuerySuggestionsIndex_AU, productsQuerySuggestionsIndex_NZ } from 'helpers/constants/algolia';

const NoResultFound = () => {
  const { formatMessage } = useFormat({ name: 'common' });
  const { locale } = useParams();
  const searchParams = useSearchParams();
  const router = useRouter();
  const searchQuery = searchParams.get('query') as string;
  const [suggestions, setSuggestions] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(true);

  const indexes = {
    products: {
      au: productsQuerySuggestionsIndex_AU,
      nz: productsQuerySuggestionsIndex_NZ,
    },
  };

  const handleSuggestion = (suggestion: string) => {
    router.push(`/search?query=${encodeURIComponent(suggestion)}`, { shallow: true });
  };
 
  return (
    <div className="relative h-[80vh] lg:h-[60vh]">
      <div className="absolute left-1/2 top-1/2 flex w-full max-w-[900px] -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center gap-0 pb-60 lg:flex-row-reverse lg:gap-50">
        {!isLoading && (
          <div className="mt-5 text-center lg:!text-left">
            <h1 className="text-32">{formatMessage({ id: 'no.resultfound', defaultMessage: 'No Result Found' })}</h1>
            <div className="d-inline mt-24">
              {formatMessage({
                id: 'no.resultfound.query',
                defaultMessage: 'No search results are found for your query:',
              })}
             <span className="font-bold"> &quot;{searchQuery ? searchQuery : ''}&quot;</span>.

              {suggestions.length > 0 && (
                <p className="d-inline">
                  {' '}
                  {formatMessage({
                    id: 'did.you.mean',
                    defaultMessage: 'Did you mean?',
                  })}{' '}
                  {suggestions.map((suggestion: any, index: any) => (
                    <>
                      <span
                        role="button"
                        onClick={() => handleSuggestion(suggestion?.query)}
                        key={index}
                        className="text-decoration-underline font-medium"
                      >
                        {suggestion?.query}
                      </span>
                      <span className="text-decoration-none">{index < suggestions.length - 1 && ', '}</span>
                    </>
                  ))}
                </p>
              )}
              {suggestions.length === 0 && (
                <p className="d-inline">
                  {' '}
                  {formatMessage({
                    id: 'search.popular.categories',
                    defaultMessage: 'You might want to search for popular categories',
                  })}{' '}
                  {['Tapware', 'Sanitaryware', 'Contura', 'Urbane II'].map((cat: any, index: any) => (
                    <>
                      <span
                        role="button"
                        onClick={() => handleSuggestion(cat)}
                        key={index}
                        className="text-decoration-underline font-medium"
                      >
                        {cat}
                      </span>
                      <span className="text-decoration-none">{index < cat.length - 1 && ', '}</span>
                    </>
                  ))}
                </p>
              )}
            </div>
            <Link link="/" className="d-block">
              <Button variant="primary" className="mt-28 px-48 py-12 text-16 font-medium">
                {formatMessage({ id: 'page.notFound.cta', defaultMessage: 'Go back home' })}
              </Button>
            </Link>
          </div>
        )}
      </div>
      <InstantSearch indexName={indexes['products'][locale as 'au' | 'nz']}>
        <Configure hitsPerPage={5} />
        <SuggestionsFetcher setSuggestions={setSuggestions} query={searchQuery} setIsLoading={setIsLoading} />
      </InstantSearch>
    </div>
  );
};

export default NoResultFound;

export function SuggestionsFetcher({ setSuggestions, query, setIsLoading }: any) {
  const { results } = useInstantSearch();
  const { refine } = useSearchBox();

  useEffect(() => {
    refine(query);
  }, [query]);

  useEffect(() => {
    if (results) {
      setSuggestions(results.hits);
    }
    if (results?.query) {
      setIsLoading && setIsLoading(false);
    }
  }, [results]);

  return null;
}
